/**
 * WCAG 2.2 contrast math.
 *
 * Kept dependency-free and platform-free (no DOM, no React Native) so both the
 * web build and the native build can import it, and so `tokens.test.ts` can
 * assert real computed ratios instead of trusting a table somebody typed by
 * hand. Algorithm: WCAG 2.2 §1.4.3 / §1.4.11 relative luminance.
 */

/** An 8-bit sRGB triple, each channel 0-255. */
export type typeRgb = {
  readonly r: number;
  readonly g: number;
  readonly b: number;
};

const HEX_PATTERN = /^#([0-9a-fA-F]{6})$/;

/**
 * Parses a 6-digit `#rrggbb` string. Throws on anything else — every color in
 * `@sportsmet/theme` is a literal 6-digit hex, so a parse failure means a typo
 * in the token table, and a silent fallback would let it ship.
 */
export const hexToRgb = (hex: string): typeRgb => {
  const match = HEX_PATTERN.exec(hex);
  if (!match) {
    throw new Error(
      `@sportsmet/theme: expected a 6-digit hex color like "#0B6E99", received ${JSON.stringify(hex)}`,
    );
  }
  const digits = match[1];
  if (digits === undefined) {
    throw new Error(
      `@sportsmet/theme: unparsable hex color ${JSON.stringify(hex)}`,
    );
  }
  return {
    r: Number.parseInt(digits.slice(0, 2), 16),
    g: Number.parseInt(digits.slice(2, 4), 16),
    b: Number.parseInt(digits.slice(4, 6), 16),
  };
};

/** Linearizes one 0-255 sRGB channel, per WCAG 2.2. */
const linearizeChannel = (channel: number): number => {
  const normalized = channel / 255;
  return normalized <= 0.04045
    ? normalized / 12.92
    : ((normalized + 0.055) / 1.055) ** 2.4;
};

/** WCAG relative luminance of a hex color, 0 (black) to 1 (white). */
export const relativeLuminance = (hex: string): number => {
  const { r, g, b } = hexToRgb(hex);
  return (
    0.2126 * linearizeChannel(r) +
    0.7152 * linearizeChannel(g) +
    0.0722 * linearizeChannel(b)
  );
};

/**
 * WCAG contrast ratio between two hex colors, 1 (identical) to 21
 * (black on white). Order-independent.
 */
export const contrastRatio = (a: string, b: string): number => {
  const luminanceA = relativeLuminance(a);
  const luminanceB = relativeLuminance(b);
  const lighter = Math.max(luminanceA, luminanceB);
  const darker = Math.min(luminanceA, luminanceB);
  return (lighter + 0.05) / (darker + 0.05);
};

/**
 * The three contrast floors this platform holds itself to.
 *
 * - `normalText` — WCAG 2.2 AA §1.4.3, body copy and anything under 18.66px/bold-14px.
 * - `largeText` — AA §1.4.3 for 24px+ or bold 18.66px+ (our display, h1, money-hero sizes).
 * - `nonText` — AA §1.4.11 for control boundaries, focus rings, and icon-only meaning.
 */
export const CONTRAST_FLOOR = {
  normalText: 4.5,
  largeText: 3,
  nonText: 3,
} as const;

export type typeContrastFloor = keyof typeof CONTRAST_FLOOR;

/** True when `foreground` on `background` clears the named WCAG 2.2 AA floor. */
export const meetsContrast = (
  foreground: string,
  background: string,
  floor: typeContrastFloor,
): boolean => contrastRatio(foreground, background) >= CONTRAST_FLOOR[floor];
